<script lang="ts" setup>
import { ref } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import type {
  AddFlightsModalInitialRoute,
  AddFligthsModalRoute,
} from '@apps/main/new-desktop/modules/addFlightsModal';
import { AddFlightsBanner } from '@apps/main/new-desktop/modules/addFlightsModal';
import { TransportSelector } from '@apps/main/new-desktop/modules/tranposrtSelector';

import type { Location } from '@libs/maps';
import { TransportType } from '@libs/trip-planner-interfaces';
import { AppButton, AppSvgIcon, SemboLineIllustrationBanner } from '@libs/ui';

interface TransportBannerProps {
  routes: AddFlightsModalInitialRoute[];
  startPositon: Location | undefined;
  endPosition: Location | undefined;
  country?: string;
}

const props = defineProps<TransportBannerProps>();

interface TransportBannerEmits {
  (eventName: 'add-flight'): void;
  (eventName: 'add-ferry'): void;
  (eventName: 'select-transport', value: TransportType): void;
  (eventName: 'add-optimized-flights', value: AddFligthsModalRoute[]): void;
}
const emit = defineEmits<TransportBannerEmits>();

const statsigEventsStore = useStatsigEventsStore();
const browserStore = useBrowserStore();
const recipeStore = useRecipeStore();

const { t } = i18n.global;

const shouldShowAddFlightsBanner = ref<boolean>(false);

function onAddFlight(): void {
  if (recipeStore.stayOnlyRecipe.length === 1) {
    shouldShowAddFlightsBanner.value = true;
  } else {
    emit('add-flight');
  }
}

function closeAddFlightsBanner(): void {
  shouldShowAddFlightsBanner.value = false;
}

function onConfirmFlights(routes: AddFligthsModalRoute[]): void {
  statsigEventsStore.logTripOverviewFlightAdded();
  closeAddFlightsBanner();
  emit('add-optimized-flights', routes);
}
</script>

<template>
  <div
    class="transport-banner"
    :class="{
      'adding-flights': shouldShowAddFlightsBanner,
    }"
  >
    <div class="transport-banner-header">
      <span class="banner-message">
        {{
          props.country
            ? t('planner.transport.ready_for_transport_dynamic', [
                props.country,
              ])
            : t('planner.transport.ready_for_transport_static')
        }}
      </span>
      <AppSvgIcon
        v-if="shouldShowAddFlightsBanner"
        class="close-icon"
        name="close"
        size="24"
        color="color-soft-black"
        @click="closeAddFlightsBanner"
      />
    </div>

    <AddFlightsBanner
      v-if="shouldShowAddFlightsBanner"
      :routes="props.routes"
      :start-positon="props.startPositon"
      :end-position="props.endPosition"
      @add-flights="onConfirmFlights"
    />
    <div v-else class="button-container">
      <AppButton
        type="secondary"
        state="enabled"
        size="small"
        @click="onAddFlight"
      >
        {{
          browserStore.isSmallerThan600px
            ? `+ ${$t('planner.transport.flight')}`
            : $t('planner.transport.add_flight')
        }}
      </AppButton>
      <AppButton
        type="secondary"
        state="enabled"
        size="small"
        @click="emit('add-ferry')"
      >
        {{
          browserStore.isSmallerThan600px
            ? `+ ${$t('planner.transport.ferry')}`
            : $t('planner.transport.add_ferry')
        }}
      </AppButton>

      <div class="button-content">
        <TransportSelector
          :transport-type="TransportType.None"
          :initial-option="
            browserStore.isSmallerThan1200px
              ? t('planner.transport.other_transports_small')
              : t('planner.transport.see_all_transports')
          "
          :disabled="false"
          is-upsell-no-transport
          is-transport-upsell-banner
          @select="emit('select-transport', $event)"
          @open="statsigEventsStore.logTripOverviewTransportSelectorOpened"
        />
      </div>
    </div>
    <SemboLineIllustrationBanner
      v-if="!shouldShowAddFlightsBanner"
      class="sembo-illustration"
    />
  </div>
</template>

<style lang="scss" scoped>
.transport-banner {
  position: relative;
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 1fr auto;
  align-items: center;
  background-color: var(--color-brand-lilac);
  margin: 0 var(--spacing-24) var(--spacing-32) var(--spacing-24);
  border-radius: var(--spacing-8);
  padding: var(--spacing-24);
  overflow: hidden;

  &.adding-flights {
    grid-template-columns: auto;
    grid-template-rows: 1fr auto;
    gap: var(--spacing-12);
    overflow: visible;
  }

  @include for-less-or-equal-than-900-px {
    padding: var(--spacing-16) var(--spacing-12);
    gap: var(--spacing-16);
    margin: 0 var(--spacing-12) var(--spacing-32) var(--spacing-12);
    grid-template-rows: auto auto;
    grid-template-columns: auto;
  }

  .transport-banner-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .close-icon {
      @include on-hover {
        cursor: pointer;
      }
    }
  }

  .banner-message {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex: 1;
    @include font-label-xl-bold;
    font-size: var(--font-size-18);
    z-index: var(--notification-z-index);
    @include for-less-or-equal-than-900-px {
      @include font-label-m-bold;
      width: 100%;
      gap: var(--spacing-16);
    }
  }

  .button-container {
    display: flex;
    gap: var(--spacing-16);
    z-index: var(--notification-z-index);
    flex-wrap: wrap;
    @include for-less-or-equal-than-900-px {
      column-gap: var(--spacing-16);
      row-gap: var(--spacing-4);
    }
    .button-content {
      display: flex;
      align-items: center;
      gap: var(--spacing-8);
    }
  }
  .sembo-illustration {
    position: absolute;
    left: 450px;
    bottom: 0;
    @include for-less-or-equal-than-900-px {
      left: -40px;
      bottom: -20px;
      transform: rotateZ(15deg);
    }
  }
  &::after {
    content: '';
    height: 35px;
    width: 0px;
    border-right: 1px dashed var(--color-neutral-70);
    position: absolute;
    bottom: -35px;
    left: 24px;
  }
}
</style>
